import React, { createContext, useCallback, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Active, closestCenter, DndContext, Over } from '@dnd-kit/core'
import { restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers'
import { arrayMove, SortableContext } from '@dnd-kit/sortable'
import { v4 as uuid4 } from 'uuid'

import { DefaultVariableResult, VariableSelectFeatureFlag } from '@makyoapp/makyo-common'
import { SimpleInput } from '@makyoapp/makyo-ui'

import { useSortableHandle } from '../../hooks/useSortableHandle'
import { RepeaterButton } from '../RepeaterButton'
import { SortableItem } from '../SortableItem'
import { VariableSelectV2 } from '../VariableSelectV2/VariableSelectV2'

export interface ColumnListValue {
  id: string
  label: string
  value?: DefaultVariableResult
}
export interface ColumnListRepeaterProps {
  id: string
  onChange: (nextValue: ColumnListValue[]) => void
  value: ColumnListValue[]
}

const ColumnListRepeaterContext = createContext<ColumnListRepeaterProps>({
  id: '',
  onChange: () => null,
  value: [],
})

export function ColumnListRepeater({ id, value, onChange }: ColumnListRepeaterProps) {
  const { t } = useTranslation()

  const fixValue = useMemo(() => {
    return Array.isArray(value)
      ? value.map((v) => {
          if (!v.id) {
            v.id = uuid4()
          }
          return v
        })
      : []
  }, [value])

  const dragEndCb = useCallback(
    (active: Active, over: Over) => {
      const oldIndex = fixValue.findIndex((p) => p.id === active.id)
      const newIndex = fixValue.findIndex((p) => p.id === over.id)
      const newValue = arrayMove(fixValue, oldIndex, newIndex)
      onChange(newValue)
    },
    [fixValue, onChange]
  )

  const { sensors, handleDragEnd } = useSortableHandle(dragEndCb)

  return (
    <ColumnListRepeaterContext.Provider
      value={{
        id,
        value: fixValue,
        onChange,
      }}
    >
      <div id={id} className='flex flex-col gap-4'>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToFirstScrollableAncestor]}
        >
          <SortableContext items={fixValue}>
            {fixValue.map((v, i) => {
              return (
                <SortableItem
                  id={v.id}
                  key={v.id}
                  itemClassName='w-full'
                  noBg
                  onDelete={() => {
                    const nextVal = [...fixValue]
                    nextVal.splice(i, 1)
                    onChange(nextVal)
                  }}
                >
                  <div className='flex-c w-full ltr:mr-2 rtl:ml-2'>
                    <div className='w-[90px] shrink-0'>
                      <SimpleInput
                        id={id ? `${id}-label` : undefined}
                        value={v.label}
                        className='h-10!'
                        inputClassName='min-w-0'
                        placeholder={t('Label')}
                        onChange={(e) => {
                          const val = e.currentTarget.value
                          const nextVal = [...fixValue]
                          nextVal[i].label = val
                          onChange(nextVal)
                        }}
                      />
                    </div>
                    <div className='w-full'>
                      <VariableSelectV2
                        value={v.value}
                        featureFlags={
                          VariableSelectFeatureFlag.SingleCell |
                          VariableSelectFeatureFlag.ColumnData |
                          VariableSelectFeatureFlag.CurrentRow |
                          VariableSelectFeatureFlag.ProfileData |
                          VariableSelectFeatureFlag.Icon |
                          VariableSelectFeatureFlag.Avatar
                        }
                        id={`${id}-value`}
                        onChange={(opt) => {
                          const nextVal = [...fixValue]
                          nextVal[i].value = opt as DefaultVariableResult
                          if (!nextVal[i].label) {
                            nextVal[i].label = VariableSelectFeatureFlag[(opt as DefaultVariableResult).flag]?.replace(
                              /([a-z])([A-Z])/g,
                              '$1 $2'
                            )
                          }
                          onChange(nextVal)
                        }}
                      />
                    </div>
                  </div>
                </SortableItem>
              )
            })}
            <RepeaterButton
              id={id ? `${id}-repeat` : undefined}
              onClick={() => {
                onChange([
                  ...fixValue,
                  {
                    id: uuid4(),
                    label: '',
                    value: undefined,
                  },
                ])
              }}
            >
              <Trans>Add Column</Trans>
            </RepeaterButton>
          </SortableContext>
        </DndContext>
      </div>
    </ColumnListRepeaterContext.Provider>
  )
}
